import React, { useState } from "react";
import styled from "styled-components";
import { useWbStoresStore } from "./use-wb-stores-store";
import { syncWbStore, getWbSyncStatus, enqueueWbJob, SyncStatusResponse } from "./wb-stores-api";
import type { WbStoreItem } from "./wb-stores-types";
import { SyncStatusPanel, WB_STEPS } from "../components/SyncStatusPanel";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e0e0e0;
  color: #888;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
`;

const StoreName = styled.div`
  font-weight: 500;
  color: #202020;
`;

const ClientInfo = styled.div`
  color: #555;
  font-size: 13px;
`;

const SellerID = styled.div`
  font-family: monospace;
  color: #888;
  font-size: 12px;
`;

const UUIDText = styled.div`
  font-family: monospace;
  color: #aaa;
  font-size: 11px;
`;

const DateText = styled.div`
  color: #aaa;
  font-size: 12px;
  white-space: nowrap;
`;

const EmptyRow = styled.tr`
  td {
    padding: 32px;
    text-align: center;
    color: #aaa;
  }
`;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function truncateUUID(uuid: string): string {
  return uuid.length > 8 ? uuid.slice(0, 8) + "..." : uuid;
}

interface RowProps {
  store: WbStoreItem;
  isExpanded: boolean;
  syncStatus: SyncStatusResponse | undefined;
  isPolling: boolean;
  onRowClick: (uuid: string) => void;
  onEnqueue: (uuid: string, type: 'initial_sync' | 'daily_sync') => void;
  onTriggerStep: (uuid: string, syncKey: string) => void;
  runningSyncs: Set<string>;
}

const StoreRow: React.FC<RowProps> = ({
  store,
  isExpanded,
  syncStatus,
  isPolling,
  onRowClick,
  onEnqueue,
  onTriggerStep,
  runningSyncs,
}) => {
  const clientLabel = store.email ?? store.public_login ?? `account #${store.account_id}`;
  const uuid = store.connection_uuid;

  return (
    <>
      <tr
        onClick={() => onRowClick(uuid)}
        style={{ cursor: 'pointer' }}
      >
        <Td>
          <StoreName>{store.store_name ?? store.marketplace_store_id}</StoreName>
        </Td>
        <Td>
          <ClientInfo>{clientLabel}</ClientInfo>
        </Td>
        <Td>
          <SellerID>{store.marketplace_store_id}</SellerID>
        </Td>
        <Td>
          <DateText>{formatDate(store.created_at)}</DateText>
        </Td>
        <Td>
          <UUIDText title={uuid}>{truncateUUID(uuid)}</UUIDText>
        </Td>
        <Td>
          {(() => {
            // Приоритет: live syncStatus (при поллинге), иначе last_job из списка
            const s = syncStatus?.job_status ?? store.last_job?.status;
            if (!s) return <span style={{ color: '#aaa' }}>—</span>;
            if (s === 'done') return <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>✓ Ок</span>;
            if (s === 'done_with_errors') return <span style={{ background: '#fff3e0', color: '#e65100', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>⚠ Частично</span>;
            if (s === 'running') return <span style={{ background: '#fff8e1', color: '#f57f17', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>⟳ Идёт</span>;
            if (s === 'failed') return <span style={{ background: '#ffebee', color: '#c62828', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>✕ Ошибка</span>;
            if (s === 'pending') return <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>○ Ожидает</span>;
            return null;
          })()}
        </Td>
        <Td>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['initial_sync', 'daily_sync'] as const).map(type => {
              const labels: Record<string, string> = { initial_sync: 'Полный', daily_sync: 'Дневной' };
              const isRunning = syncStatus?.job_status === 'running'
                             || syncStatus?.job_status === 'pending'
                             || isPolling;
              return (
                <button
                  key={type}
                  disabled={isRunning}
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEnqueue(uuid, type); }}
                  style={{
                    background: type === 'initial_sync' ? '#1565c0' : '#388e3c',
                    color: 'white', border: 'none', borderRadius: 4,
                    padding: '3px 8px', fontSize: 11, cursor: isRunning ? 'not-allowed' : 'pointer',
                    opacity: isRunning ? 0.5 : 1,
                  }}
                >
                  ⟳ {labels[type]}
                </button>
              );
            })}
          </div>
        </Td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} style={{ padding: 0 }}>
            <SyncStatusPanel
              status={syncStatus || null}
              steps={WB_STEPS}
              onTriggerStep={key => onTriggerStep(uuid, key)}
              finishedAt={syncStatus?.finished_at}
              runningSyncs={runningSyncs}
            />
          </td>
        </tr>
      )}
    </>
  );
};

export const WbStoresTable: React.FC = () => {
  const { stores, loading } = useWbStoresStore();

  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);
  const [syncStatuses, setSyncStatuses] = useState<Record<string, SyncStatusResponse>>({});
  const [pollingIntervals, setPollingIntervals] = useState<Record<string, NodeJS.Timeout>>({});
  const [runningSyncs, setRunningSyncs] = useState<Set<string>>(new Set());

  const handleRowExpand = async (uuid: string) => {
    if (expandedUuid === uuid) {
      // Сворачиваем — чистим поллинг
      if (pollingIntervals[uuid]) {
        clearInterval(pollingIntervals[uuid]);
        setPollingIntervals(prev => { const n = { ...prev }; delete n[uuid]; return n; });
      }
      setExpandedUuid(null);
      return;
    }
    setExpandedUuid(uuid);
    try {
      const status = await getWbSyncStatus(uuid);
      setSyncStatuses(prev => ({ ...prev, [uuid]: status }));

      // Если джоба бежит — запускаем поллинг
      if (status.job_status === 'running') {
        const interval = setInterval(async () => {
          try {
            const s = await getWbSyncStatus(uuid);
            setSyncStatuses(prev => ({ ...prev, [uuid]: s }));
            if (s.job_status === 'done' || s.job_status === 'failed') {
              clearInterval(interval);
              setPollingIntervals(prev => { const n = { ...prev }; delete n[uuid]; return n; });
            }
          } catch {
            clearInterval(interval);
            setPollingIntervals(prev => { const n = { ...prev }; delete n[uuid]; return n; });
          }
        }, 5000);
        setPollingIntervals(prev => ({ ...prev, [uuid]: interval }));
      }
    } catch (e) {
      console.error('Не удалось загрузить статус синка', e);
    }
  };

  const handleEnqueue = async (
    uuid: string,
    type: 'initial_sync' | 'daily_sync'
  ) => {
    try {
      await enqueueWbJob(uuid, type);
      const interval = setInterval(async () => {
        try {
          const status = await getWbSyncStatus(uuid);
          setSyncStatuses(prev => ({ ...prev, [uuid]: status }));
          if (status.job_status === 'done' || status.job_status === 'failed') {
            clearInterval(interval);
            setPollingIntervals(prev => { const n = { ...prev }; delete n[uuid]; return n; });
          }
        } catch (e) {
          clearInterval(interval);
          setPollingIntervals(prev => { const n = { ...prev }; delete n[uuid]; return n; });
        }
      }, 5000);
      setTimeout(() => {
        clearInterval(interval);
        setPollingIntervals(prev => { const n = { ...prev }; delete n[uuid]; return n; });
      }, 10 * 60 * 1000);
      setPollingIntervals(prev => ({ ...prev, [uuid]: interval }));
    } catch (e: any) {
      if (e.message === 'CONFLICT') {
        alert('Синк уже запущен для этого магазина');
      }
    }
  };

  const handleTriggerStep = async (uuid: string, syncKey: string) => {
    const resultName = WB_STEPS.find(s => s.syncKey === syncKey)?.resultName ?? syncKey;

    setRunningSyncs(prev => new Set(prev).add(syncKey));
    try {
      await syncWbStore(uuid, syncKey as any);
      setSyncStatuses(prev => ({
        ...prev,
        [uuid]: {
          ...(prev[uuid] || { steps: [] }),
          steps: [
            ...(prev[uuid]?.steps || []).filter(s => s.name !== resultName),
            { name: resultName, status: 'ok' },
          ],
        },
      }));
    } catch (e) {
      setSyncStatuses(prev => ({
        ...prev,
        [uuid]: {
          ...(prev[uuid] || { steps: [] }),
          steps: [
            ...(prev[uuid]?.steps || []).filter(s => s.name !== resultName),
            { name: resultName, status: 'error: failed' },
          ],
        },
      }));
    } finally {
      setRunningSyncs(prev => {
        const next = new Set(prev);
        next.delete(syncKey);
        return next;
      });
    }
  };

  return (
    <Table>
      <thead>
        <tr>
          <Th>Магазин</Th>
          <Th>Клиент</Th>
          <Th>Seller ID</Th>
          <Th>Добавлен</Th>
          <Th>UUID</Th>
          <Th>Статус</Th>
          <Th>Процессоры</Th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <EmptyRow>
            <td colSpan={7}>Загрузка...</td>
          </EmptyRow>
        )}
        {!loading && stores.length === 0 && (
          <EmptyRow>
            <td colSpan={7}>Нет WB магазинов</td>
          </EmptyRow>
        )}
        {!loading && stores.map((store) => (
          <StoreRow
            key={store.connection_uuid}
            store={store}
            isExpanded={expandedUuid === store.connection_uuid}
            syncStatus={syncStatuses[store.connection_uuid]}
            isPolling={!!pollingIntervals[store.connection_uuid]}
            onRowClick={handleRowExpand}
            onEnqueue={handleEnqueue}
            onTriggerStep={handleTriggerStep}
            runningSyncs={runningSyncs}
          />
        ))}
      </tbody>
    </Table>
  );
};
